import React from 'react';
import { CircularProgress } from '@material-ui/core';
import Card from '@material-ui/core/Card';
import Typography from '@material-ui/core/Typography';
import AddIcon from '@material-ui/icons/AddCircleOutline';
import RemoveIcon from '@material-ui/icons/RemoveCircleOutline';
import { makeStyles } from '@material-ui/styles';
import { colors, elevation } from '@skyslope/mache';
import { pluralize, type Nullable } from '@skyslope/utils';
import cs from 'classnames';
import { twMerge } from 'tailwind-merge';
import type { ITemplateMetadata } from '@/store/templateManagement/types';
import Search from '../common/Search';

const useStyles = makeStyles(() => ({
  template: {
    boxShadow: elevation.active[6],
    '&:hover': {
      boxShadow: elevation.active[12],
    },
  },
  appliedTemplate: {
    border: `1px solid ${colors.grey[500]}`,
    borderLeft: `3px solid ${colors.blue[800]}`,
    backgroundColor: colors.blue[50],
    paddingLeft: 15,
  },
  templateText: {
    overflow: 'hidden',
    marginRight: '16px',
  },

  templateName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

export interface AppliedTemplate {
  template: string;
  data: {
    document: string;
  };
}

interface TemplatesListProps {
  templates: Nullable<ITemplateMetadata[]>;
  isLoadingTemplates: boolean;
  selectedTemplate: Nullable<ITemplateMetadata>;
  onSelectTemplate: (template: ITemplateMetadata, wasJustSelected: boolean) => void;
  classes?: {
    search?: string;
    cardContainer?: string;
  };
}

export function TemplatesList(props: TemplatesListProps) {
  const { templates, isLoadingTemplates, selectedTemplate, onSelectTemplate, classes } = props;
  const selectedTemplateId = selectedTemplate?.id;

  const styles = useStyles();

  const [search, setSearch] = React.useState('');
  const filteredTemplates = React.useMemo(
    () => templates?.filter((t) => t.name.toLowerCase().includes(search.toLowerCase())),
    [templates, search]
  );

  return (
    <>
      <Search value={search} onChange={setSearch} className={twMerge('my-4 px-4 w-auto', classes?.search)} />

      {!isLoadingTemplates && !filteredTemplates?.length ? (
        <Typography variant="body1" align="center">
          {search.length ? 'No templates found' : 'No templates have been created yet'}
        </Typography>
      ) : null}

      <div className={twMerge('flex flex-col gap-4 px-4 pb-8 grow overflow-auto', classes?.cardContainer)}>
        {filteredTemplates?.map((template) => (
          <Card
            key={template.id}
            className={cs(
              'flex items-center justify-between py-3 px-4 cursor-pointer',
              styles.template,
              template.id === selectedTemplateId && styles.appliedTemplate,
              'flex-shrink-0'
            )}
            onClick={() => onSelectTemplate(template, template.id !== selectedTemplateId)}
            title={template.name}
          >
            <div className="overflow-hidden mr-4 select-none">
              <Typography variant="body1" className={styles.templateName}>
                {template.name}
              </Typography>
              <Typography variant="overline" className="text-grey-500 uppercase">
                {template.metadata.numberOfSigners} {pluralize('signer', template.metadata.numberOfSigners)}
              </Typography>
            </div>

            {template.id === selectedTemplateId ? (
              <RemoveIcon className="text-grey-800" />
            ) : (
              <AddIcon className="text-blue-800" />
            )}
          </Card>
        ))}

        {isLoadingTemplates && (
          <div className="flex justify-center p-4">
            <CircularProgress />
          </div>
        )}
      </div>
    </>
  );
}
